import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getIsAuth } from '@/entities/Admin';
import {
  handleNotificationsUnreadCountUpdated,
  NOTIFICATION_SOCKET_EVENT,
  resolveNotificationSocketPayload,
  type NotificationSocketUnreadCountPayload,
} from '@/entities/MyNotifications';
import { useAppDispatch } from '@/shared/lib/hooks/useAppDispatch';
import { LocalStorageService } from '@/shared/services/localStorage.service';
import {
  clearChatRealtimePending,
  emitChatRealtime,
  emitChatRealtimeReconnect,
} from './chatRealtimeBus';
import { CHAT_SOCKET_EVENT } from './chatSocketTypes';
import { destroyChatSocketClient, getChatSocketClient } from './getChatSocketClient';

async function subscribeListWatch(reason: string) {
  try {
    await getChatSocketClient().subscribeChatList([]);
    console.log('[ChatSocket] chat-list:subscribe', { chatIds: [], reason });
  } catch (error) {
    console.error('[ChatSocket] chat-list:subscribe failed', { chatIds: [], reason }, error);
  }
}

export function useChatSocketLifecycle() {
  const dispatch = useAppDispatch();
  const isAuth = useSelector(getIsAuth);
  const accessToken = LocalStorageService.getAccessToken();
  const enabled = isAuth || !!accessToken;

  useEffect(() => {
    if (!enabled) {
      clearChatRealtimePending();
      destroyChatSocketClient();
      return;
    }

    let client: ReturnType<typeof getChatSocketClient>;

    try {
      client = getChatSocketClient();
      client.connect();
    } catch (error) {
      console.error('[ChatSocket] connect failed', error);
      return;
    }

    void subscribeListWatch('auth connect');

    const ensureConnected = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      const currentClient = getChatSocketClient();
      if (currentClient.isConnected()) {
        return;
      }

      try {
        currentClient.reconnectWithFreshToken();
      } catch (error) {
        console.error('[ChatSocket] reconnect failed', error);
      }
    };

    let isFirstConnect = true;

    const offConnect = client.onConnect(() => {
      console.log('[ChatSocket] connected');
      void subscribeListWatch(isFirstConnect ? 'initial connect' : 'socket reconnect');

      if (!isFirstConnect) {
        emitChatRealtimeReconnect();
      }

      isFirstConnect = false;
    });

    const offDisconnect = client.onDisconnect((reason) => {
      console.log('[ChatSocket] disconnected', reason);
    });

    const offEvents = Object.values(CHAT_SOCKET_EVENT).map((event) =>
      client.onChatEvent(event, (envelope) => {
        emitChatRealtime(envelope);
      }),
    );

    // Счётчик непрочитанных уведомлений бэкенд шлёт при подключении и после каждого изменения
    const offNotificationsUnreadCount = client.onNotificationEvent(
      NOTIFICATION_SOCKET_EVENT.UnreadCount,
      (data) => {
        const payload =
          resolveNotificationSocketPayload<NotificationSocketUnreadCountPayload>(data);
        if (payload && typeof payload.count === 'number') {
          handleNotificationsUnreadCountUpdated(dispatch, payload.count);
        }
      },
    );

    const handleResume = () => ensureConnected();

    document.addEventListener('visibilitychange', handleResume);
    window.addEventListener('focus', handleResume);

    return () => {
      offConnect();
      offDisconnect();
      offEvents.forEach((off) => off());
      offNotificationsUnreadCount();
      document.removeEventListener('visibilitychange', handleResume);
      window.removeEventListener('focus', handleResume);
      clearChatRealtimePending();
      destroyChatSocketClient();
    };
  }, [dispatch, enabled]);
}
